import { cn } from "@/lib/utils";
import { AnimatedList } from "@/components/ui/animated-list";
import { Logo } from "@/components/ui/logo";
import { useTranslation } from "@/hooks/useTranslation";

interface Item {
  name: string;
  description: string;
  time: string;
}

type NotificationProps = Item & {
  enableAnimation?: boolean;
};

export const Notification = ({
  name,
  description,
  time,
  enableAnimation = true,
}: NotificationProps) => {
  return (
    <figure
      className={cn(
        "relative mx-auto min-h-fit w-full max-w-[400px] cursor-pointer overflow-hidden rounded-xl p-3",
        // animation styles
        {
          "transition-all duration-200 ease-in-out hover:scale-[101%]": enableAnimation,
        },
        // iOS-like styles
        "bg-white/90 backdrop-blur-sm border border-gray-200/30",
        "shadow-sm",
        // dark styles
        "dark:bg-black dark:border-[#38383a]/50 dark:text-white",
      )}
    >
      <div className="flex flex-row items-center gap-3">
        <div className="flex-shrink-0">
          <Logo className="size-10 min-w-8 min-h-8" iconClassName="rotate-0" />
        </div>
        <div className="flex flex-col overflow-hidden flex-grow">
          <div className="flex justify-between items-center w-full">
            <figcaption className="font-semibold text-sm dark:text-white">
              ProgressMade.ai
            </figcaption>
            <span className="text-xs text-gray-500">{time}</span>
          </div>
          <div className="text-sm font-medium mt-0.5">{name}</div>
          <p className="text-sm font-normal text-gray-700 dark:text-white/70 mt-0.5 line-clamp-2">
            {description}
          </p>
        </div>
      </div>
    </figure>
  );
};

export function ProgressNotifications({
  className,
}: {
  className?: string;
}) {
  const { t } = useTranslation();

  const notifications = [
    {
      id: "1",
      name: t("progress_notifications.daily_goal_achieved"),
      description: t("progress_notifications.daily_goal_achieved_description"),
      time: t("progress_notifications.time_15m_ago"),
    },
    {
      id: "2",
      name: t("progress_notifications.water_reminder"),
      description: t("progress_notifications.water_reminder_description"),
      time: t("progress_notifications.time_10m_ago"),
    },
    {
      id: "3",
      name: t("progress_notifications.workout_completed"),
      description: t("progress_notifications.workout_completed_description"),
      time: t("progress_notifications.time_5m_ago"),
    },
    {
      id: "4",
      name: t("progress_notifications.new_achievement"),
      description: t("progress_notifications.new_achievement_description"),
      time: t("progress_notifications.time_2m_ago"),
    },
    {
      id: "5",
      name: t("progress_notifications.nutrition_goal"),
      description: t("progress_notifications.nutrition_goal_description"),
      time: t("progress_notifications.time_just_now"),
    },
    {
      id: "6",
      name: t("progress_notifications.todays_routines"),
      description: t("progress_notifications.todays_routines_description"),
      time: t("progress_notifications.time_4h_ago"),
    },
    {
      id: "7",
      name: t("progress_notifications.meditation_reminder"),
      description: t("progress_notifications.meditation_reminder_description"),
      time: t("progress_notifications.time_3m_ago"),
    },
    {
      id: "8",
      name: t("progress_notifications.weekly_progress"),
      description: t("progress_notifications.weekly_progress_description"),
      time: t("progress_notifications.time_1h_ago"),
    },
    {
      id: "9",
      name: t("progress_notifications.consistency_reward"),
      description: t("progress_notifications.consistency_reward_description"),
      time: t("progress_notifications.time_2h_ago"),
    },
    {
      id: "10",
      name: t("progress_notifications.rest_day"),
      description: t("progress_notifications.rest_day_description"),
      time: t("progress_notifications.time_30m_ago"),
    },
  ];

  // Repeat the notifications array to create the infinite loop effect
  const repeatedNotifications = Array.from({ length: 6 }, () => notifications).flat();

  return (
    <div
      className={cn(
        "h-full flex flex-col bg-gradient-to-b from-lime-100 to-emerald-100 dark:from-lime-900/30 dark:to-emerald-900/30 h-[280px] md:h-[420px] pt-4 px-4 w-full flex-col overflow-hidden",
        className,
      )}
    >
      <div className="flex-1 relative overflow-hidden">
        <AnimatedList infiniteLoop>
          {repeatedNotifications.map((item, index) => (
            <Notification {...item} key={`${item.id}-${index}`} />
          ))}
        </AnimatedList>
      </div>
    </div>
  );
}
